/**
 * User Ratings Service
 * Stores user ratings for movies/shows to improve Gemini recommendations
 */

export interface UserRating {
  id: number;
  mediaType: 'movie' | 'tv';
  title: string;
  rating: number; // 1-5, supports half stars
  posterPath?: string;
  ratedAt: number;
}

const RATINGS_KEY = 'cinevault_user_ratings';

// Get all user ratings
export function getUserRatings(): UserRating[] {
  try {
    const saved = localStorage.getItem(RATINGS_KEY);
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

// Save all ratings
function saveRatings(ratings: UserRating[]): void {
  localStorage.setItem(RATINGS_KEY, JSON.stringify(ratings));
}

// Get rating for a specific item
export function getRating(mediaType: 'movie' | 'tv', id: number): number {
  const ratings = getUserRatings();
  const found = ratings.find(r => r.mediaType === mediaType && r.id === id);
  return found?.rating || 0;
}

// Set rating for an item
export function setRating(
  mediaType: 'movie' | 'tv',
  id: number,
  title: string,
  rating: number,
  posterPath?: string
): void {
  const ratings = getUserRatings();

  const newRating: UserRating = {
    id,
    mediaType,
    title,
    rating,
    posterPath,
    ratedAt: Date.now(),
  };

  const existingIndex = ratings.findIndex(r => r.mediaType === mediaType && r.id === id);
  const updatedRatings = existingIndex >= 0
    ? ratings.map((r, i) => i === existingIndex ? newRating : r)
    : [...ratings, newRating];

  saveRatings(updatedRatings);
}

// Remove rating
export function removeRating(mediaType: 'movie' | 'tv', id: number): void {
  const ratings = getUserRatings();
  const filtered = ratings.filter(r => !(r.mediaType === mediaType && r.id === id));
  saveRatings(filtered);
}

// Check if item has been rated (watched)
export function hasBeenRated(mediaType: 'movie' | 'tv', id: number): boolean {
  return getRating(mediaType, id) > 0;
}

// Get ratings by score range
export function getRatingsByScore(minRating: number, maxRating: number = 5): UserRating[] {
  return getUserRatings().filter(r => r.rating >= minRating && r.rating <= maxRating);
}

// Get loved items (4.5+ stars)
export function getLovedItems(): UserRating[] {
  return getRatingsByScore(4.5);
}

// Get liked items (3.5-4.5 stars)
export function getLikedItems(): UserRating[] {
  return getRatingsByScore(3.5, 4.4);
}

// Get disliked items (below 2.5 stars)
export function getDislikedItems(): UserRating[] {
  return getRatingsByScore(0, 2.4);
}

// Generate context string for Gemini
export function getRatingsContextForGemini(): string {
  const loved = getLovedItems();
  const liked = getLikedItems();
  const disliked = getDislikedItems();

  let context = '';

  if (loved.length > 0) {
    context += `\nLOVED (${loved.length}): ${loved.map(r => `${r.title} (${r.rating}★)`).join(', ')}`;
  }

  if (liked.length > 0) {
    context += `\nLIKED (${liked.length}): ${liked.slice(0, 10).map(r => `${r.title} (${r.rating}★)`).join(', ')}`;
  }

  if (disliked.length > 0) {
    context += `\nDISLIKED (${disliked.length}): ${disliked.map(r => r.title).join(', ')}`;
  }

  return context || '\nNo ratings yet.';
}

// Get stats
export function getRatingStats(): {
  total: number;
  avgRating: number;
  loved: number;
  liked: number;
  disliked: number;
} {
  const ratings = getUserRatings();
  const total = ratings.length;

  if (total === 0) {
    return { total: 0, avgRating: 0, loved: 0, liked: 0, disliked: 0 };
  }

  const avgRating = ratings.reduce((sum, r) => sum + r.rating, 0) / total;

  return {
    total,
    avgRating: Math.round(avgRating * 10) / 10,
    loved: getLovedItems().length,
    liked: getLikedItems().length,
    disliked: getDislikedItems().length,
  };
}
